import React, { useState } from "react";
import styled from "styled-components";
import { FaPhone, FaPhoneSlash } from "react-icons/fa";
import { useNotifications } from '../../../contexts/notification';

const NotificationContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  width: 320px;
  z-index: 1000;
  animation: ${(props) =>
    props.isExiting
      ? "slideOut 0.3s ease-out forwards"
      : "slideIn 0.3s ease-out"};

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;

const CallerInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;

  img {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    margin-right: 1rem;
    object-fit: cover;
  }

  .details {
    flex: 1;

    h4 {
      margin: 0 0 0.25rem 0;
      color: #333;
    }

    p {
      margin: 0;
      color: #7f8c8d;
      font-size: 0.875rem;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 5px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 500;
  margin: 0 0.5rem;
  transition: background-color 0.2s;

  svg {
    margin-right: 0.5rem;
  }

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }
`;

const AcceptButton = styled(ActionButton)`
  background-color: #4caf50;
  color: white;

  &:hover {
    background-color: #43a047;
  }
`;

const RejectButton = styled(ActionButton)`
  background-color: #f44336;
  color: white;

  &:hover {
    background-color: #e53935;
  }
`;

const VideoCallNotification = ({ caller, onAccept, onReject, isExiting }) => {
  const [display] = useState(true);
  const { addNotification } = useNotifications();

  const handleAccept = () => {
    // Add notification that call was accepted
    addNotification({
      title: "Call Accepted",
      message: `You accepted a call from ${caller.name}`,
      type: "success",
      forRole: "proStudent"
    });

    if (onAccept) onAccept(caller);
  };

  const handleReject = () => {
    // Add notification that call was rejected
    addNotification({
      title: "Call Rejected",
      message: `You rejected a call from ${caller.name}`,
      type: "info",
      forRole: "proStudent"
    });

    if (onReject) onReject(caller);
  };

  if (!display) return null;

  return (
    <NotificationContainer isExiting={isExiting}>
      <CallerInfo>
        <img
          src={caller.avatar || "https://via.placeholder.com/50"}
          alt={caller.name}
        />
        <div className="details">
          <h4>Incoming Call</h4>
          <p>
            {caller.name} {caller.role ? `(${caller.role})` : ""}
          </p>
        </div>
      </CallerInfo>
      <ButtonContainer>
        <AcceptButton onClick={handleAccept}>
          <FaPhone />
          Accept
        </AcceptButton>
        <RejectButton onClick={handleReject}>
          <FaPhoneSlash />
          Decline
        </RejectButton>
      </ButtonContainer>
    </NotificationContainer>
  );
};

export default VideoCallNotification;
